import { useState, useEffect } from 'react';
import { Trophy, Loader2, LayoutGrid, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getKnockout, getGroups, getTeams, getSettings, getMatches } from '../../lib/api';
import { cn } from '../../lib/utils';

const TeamBadge = ({ team, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'text-xs py-1 px-2',
    md: 'text-sm py-2 px-3',
  };

  return (
    <div className={cn(
      "rounded-lg font-semibold truncate",
      sizeClasses[size],
      team?.name ? "bg-slate-800/50 text-slate-200" : "bg-slate-800/30 text-slate-500"
    )}>
      {team?.name || 'TBD'}
    </div>
  );
};

const BracketMatch = ({ match, showLeg = true }) => {
  if (!match) return null;

  return (
    <div className="bg-slate-950/50 rounded-lg border border-slate-800 p-2.5">
      {showLeg && match.leg && (
        <div className="text-[10px] text-slate-500 mb-1.5 uppercase tracking-wide">
          {match.leg === 1 ? 'Andata' : 'Ritorno'}
        </div>
      )}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <TeamBadge team={match.home_team} />
          <span className="font-numbers text-base w-6 text-center">
            {match.is_played ? match.home_score : '-'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TeamBadge team={match.away_team} />
          <span className="font-numbers text-base w-6 text-center">
            {match.is_played ? match.away_score : '-'}
          </span>
        </div>
      </div>
      {!match.is_played && match.match_date && (
        <div className="text-[10px] text-slate-500 mt-1.5">
          {new Date(match.match_date).toLocaleDateString('it-IT', {
            day: 'numeric', month: 'short', timeZone: 'UTC'
          })}
        </div>
      )}
    </div>
  );
};

export const AdminTabellonePage = () => {
  const [knockout, setKnockout] = useState(null);
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [knockoutRes, groupsRes, teamsRes, settingsRes, matchesRes] = await Promise.all([
          getKnockout().catch(() => ({ data: { quarterfinals: [], semifinals: [], final_3_4: null, final_1_2: null } })),
          getGroups(),
          getTeams(),
          getSettings().catch(() => ({ data: {} })),
          getMatches().catch(() => ({ data: [] })),
        ]);
        setKnockout(knockoutRes.data);
        setGroups(groupsRes.data);
        setTeams(teamsRes.data);
        setSettings(settingsRes.data || {});
        setMatches(matchesRes.data || []);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const groupByMatchNumber = (matches) => {
    const grouped = {};
    for (const match of matches || []) {
      const num = match.match_number || 0;
      if (!grouped[num]) grouped[num] = { leg1: null, leg2: null };
      if (match.leg === 1) grouped[num].leg1 = match;
      else if (match.leg === 2) grouped[num].leg2 = match;
    }
    return grouped;
  };

  const getQualifiedTeams = () => {
    const qualified = [];
    for (const group of groups) {
      const groupTeams = teams
        .filter(t => t.group_id === group.id)
        .sort((a, b) => (b.points || 0) - (a.points || 0) || (b.goal_difference || 0) - (a.goal_difference || 0));
      if (groupTeams.length >= 2) {
        qualified.push({ group: group.name, first: groupTeams[0], second: groupTeams[1] });
      }
    }
    return qualified;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const qfGroups = groupByMatchNumber(knockout?.quarterfinals);
  const sfGroups = groupByMatchNumber(knockout?.semifinals);
  const hasKnockout = knockout && (
    knockout.quarterfinals?.length > 0 || knockout.semifinals?.length > 0 ||
    knockout.final_3_4 || knockout.final_1_2
  );
  const qualifiedTeams = getQualifiedTeams();
  // Live: appena qualcuno ha giocato, mostra zona qualificazione attuale
  const groupPhaseComplete = teams.some(t => (t.played || 0) > 0);

  const printA0 = () => {
    const teamById = Object.fromEntries(teams.map(t => [t.id, t]));

    const fmtDate = (iso) => {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
    };

    const fmtDateTime = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const datePart = d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit', timeZone: 'UTC' }).toUpperCase();
      const timePart = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
      return `${datePart} ${timePart}`;
    };

    // Build group blocks (sorted A,B,C)
    const groupBlocks = (groups || []).map((g) => ({
      id: g.id,
      name: g.name,
      teams: teams.filter(t => t.group_id === g.id),
      matches: matches
        .filter(m => m.phase === 'group' && m.group_id === g.id)
        .sort((a, b) => new Date(a.match_date) - new Date(b.match_date)),
    })).sort((a, b) => a.name.localeCompare(b.name));

    // 9 quadratini per squadra (3 partite × 3 punti vittoria),
    // raggruppati 3+3+3 con piccolo gap fra le partite
    const pointBoxes = () => `
      <div class="pt-row">
        ${[1,2,3].map(() => `
          <div class="pt-group">
            <span class="pt-square"></span>
            <span class="pt-square"></span>
            <span class="pt-square"></span>
          </div>
        `).join('')}
        <div class="pt-tot"><div class="pt-label">TOT</div><div class="pt-tot-box"></div></div>
      </div>`;

    const groupCard = (g) => {
      const teamRows = [0,1,2,3].map(i => {
        const t = g.teams[i];
        return `
          <tr>
            <td class="pos">${i+1}</td>
            <td class="tname">${t?.name || '________________'}</td>
            <td class="pt-col">${pointBoxes()}</td>
          </tr>`;
      }).join('');

      const matchRows = (g.matches.length > 0 ? g.matches : Array(6).fill(null)).map((m, i) => {
        const home = m ? (teamById[m.home_team_id]?.name || '____________') : '____________';
        const away = m ? (teamById[m.away_team_id]?.name || '____________') : '____________';
        const date = m ? fmtDateTime(m.match_date) : '__________________';
        return `
          <div class="mrow">
            <div class="mteam home">${home}</div>
            <div class="mscore"><span class="mbox"></span><span class="msep">–</span><span class="mbox"></span></div>
            <div class="mteam away">${away}</div>
            <div class="mdate">${date}</div>
          </div>`;
      }).join('');

      return `
        <div class="group-card">
          <div class="group-title">GIRONE ${g.name}</div>
          <table class="group-table">
            <thead>
              <tr>
                <th class="pos">#</th>
                <th class="tname">Squadra</th>
                <th class="pt-col">Punti (3 per vittoria, 1 per pareggio)</th>
              </tr>
            </thead>
            <tbody>${teamRows}</tbody>
          </table>
          <div class="group-matches-title">Partite del Girone ${g.name}</div>
          <div class="group-matches">${matchRows}</div>
        </div>`;
    };

    // Knockout pairings labels
    const fg = settings?.knockout_draw_first_group;
    const sg = settings?.knockout_draw_second_group;
    const otherFirsts = fg ? ['A','B','C'].filter(x => x !== fg).sort() : [];
    const otherSeconds = sg ? ['A','B','C'].filter(x => x !== sg).sort() : [];

    // Date fallback dalle settings.match_schedule (programmazione di sistema)
    const schedule = settings?.match_schedule || [];
    const schedSlot = (re, slotIdx = 0) => {
      const e = schedule.find(s => re.test((s.label || '').toLowerCase()));
      if (!e) return null;
      const times = (e.times || []).slice().sort();
      const t = times[slotIdx] || times[times.length - 1] || '20:30';
      return `${e.date}T${t}:00Z`;
    };
    // 1 Quarto = QF1 + QF2 (slot 0 e 1), 2 Quarto = QF3 + QF4 (slot 0 e 1)
    const qf1AndataD = schedSlot(/^1.*quarto.*andata/, 0);
    const qf1RitornoD = schedSlot(/^1.*quarto.*ritorno/, 0);
    const qf2AndataD = schedSlot(/^1.*quarto.*andata/, 1);
    const qf2RitornoD = schedSlot(/^1.*quarto.*ritorno/, 1);
    const qf3AndataD = schedSlot(/^2.*quarto.*andata/, 0);
    const qf3RitornoD = schedSlot(/^2.*quarto.*ritorno/, 0);
    const qf4AndataD = schedSlot(/^2.*quarto.*andata/, 1);
    const qf4RitornoD = schedSlot(/^2.*quarto.*ritorno/, 1);
    const sf1AndataD = schedSlot(/semifinal.*andata/, 0);
    const sf1RitornoD = schedSlot(/semifinal.*ritorno/, 0);
    const sf2AndataD = schedSlot(/semifinal.*andata/, 1);
    const sf2RitornoD = schedSlot(/semifinal.*ritorno/, 1);
    // Finali stesso giorno: 3°/4° PRIMA (slot 0), 1°/2° DOPO (slot 1)
    const final34D = schedSlot(/^finale$/, 0);
    const final12D = schedSlot(/^finale$/, 1);

    const pickTwoLeg = (pair, fallbackD1, fallbackD2) => {
      const ref = pair?.leg1 || pair?.leg2;
      if (!ref) return null;
      return {
        home: ref.home_team?.name || teamById[ref.home_team_id]?.name,
        away: ref.away_team?.name || teamById[ref.away_team_id]?.name,
        date1: pair?.leg1?.match_date || fallbackD1 || null,
        date2: pair?.leg2?.match_date || fallbackD2 || null,
        twoLeg: true,
      };
    };

    const qfData = [
      pickTwoLeg(qfGroups[1], qf1AndataD, qf1RitornoD) || { home: null, away: null, homeHint: '1°C', awayHint: '2°A', date1: qf1AndataD, date2: qf1RitornoD, twoLeg: true },
      pickTwoLeg(qfGroups[2], qf2AndataD, qf2RitornoD) || { home: null, away: null, homeHint: '1°A', awayHint: '1ª miglior 3ª', date1: qf2AndataD, date2: qf2RitornoD, twoLeg: true },
      pickTwoLeg(qfGroups[3], qf3AndataD, qf3RitornoD) || { home: null, away: null, homeHint: '1°B', awayHint: '2ª miglior 3ª', date1: qf3AndataD, date2: qf3RitornoD, twoLeg: true },
      pickTwoLeg(qfGroups[4], qf4AndataD, qf4RitornoD) || { home: null, away: null, homeHint: '2°B', awayHint: '2°C', date1: qf4AndataD, date2: qf4RitornoD, twoLeg: true },
    ];

    const sfData = [
      pickTwoLeg(sfGroups[1], sf1AndataD, sf1RitornoD) || { home: null, away: null, homeHint: 'Vincente QF1', awayHint: 'Vincente QF2', date1: sf1AndataD, date2: sf1RitornoD, twoLeg: true },
      pickTwoLeg(sfGroups[2], sf2AndataD, sf2RitornoD) || { home: null, away: null, homeHint: 'Vincente QF3', awayHint: 'Vincente QF4', date1: sf2AndataD, date2: sf2RitornoD, twoLeg: true },
    ];

    const finalMatch = knockout?.final_1_2
      ? { home: knockout.final_1_2.home_team?.name, away: knockout.final_1_2.away_team?.name, date: knockout.final_1_2.match_date || final12D }
      : { home: null, away: null, homeHint: 'Vincente SF1', awayHint: 'Vincente SF2', date: final12D };
    const final34 = knockout?.final_3_4
      ? { home: knockout.final_3_4.home_team?.name, away: knockout.final_3_4.away_team?.name, date: knockout.final_3_4.match_date || final34D }
      : { home: null, away: null, homeHint: 'Perdente SF1', awayHint: 'Perdente SF2', date: final34D };

    const teamCell = (name, hint, side) => {
      if (name) return `<div class="kteam ${side}">${name}</div>`;
      return `
        <div class="kteam-slot ${side}">
          <div class="kline"></div>
          ${hint ? `<div class="khint">${hint}</div>` : ''}
        </div>`;
    };

    const fmtDT = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const datePart = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
      const timePart = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
      return `${datePart} · ${timePart}`;
    };

    const kMatchBox = (m, label) => {
      if (m.twoLeg) {
        return `
          <div class="kbox">
            <div class="klabel">${label}</div>
            <div class="kdates">
              <div class="kdate-row"><span class="kleg-tag">Andata</span> <span class="kdate-val">${m.date1 ? fmtDT(m.date1) : '________________'}</span></div>
              <div class="kdate-row"><span class="kleg-tag">Ritorno</span> <span class="kdate-val">${m.date2 ? fmtDT(m.date2) : '________________'}</span></div>
            </div>
            <div class="kteams">
              ${teamCell(m.home, m.homeHint, '')}
              <div class="kscore-col">
                <div class="kleg-row"><span class="kleg-tag">A</span><span class="kbox-score"></span><span class="ksep">–</span><span class="kbox-score"></span></div>
                <div class="kleg-row"><span class="kleg-tag">R</span><span class="kbox-score"></span><span class="ksep">–</span><span class="kbox-score"></span></div>
              </div>
              ${teamCell(m.away, m.awayHint, 'right')}
            </div>
          </div>`;
      }
      return `
        <div class="kbox">
          <div class="klabel">${label}</div>
          <div class="kdate">Data: ${m.date ? fmtDT(m.date) : '________________'}</div>
          <div class="kteams">
            ${teamCell(m.home, m.homeHint, '')}
            <div class="kscore"><span class="kbox-score"></span><span class="ksep">–</span><span class="kbox-score"></span></div>
            ${teamCell(m.away, m.awayHint, 'right')}
          </div>
        </div>`;
    };

    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Tabellone Torneo Notturno Viadana Bresciana</title>
<style>
  @page { size: A0 portrait; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: 'Helvetica Neue', Arial, sans-serif; }
  .sheet { width: 841mm; height: 1189mm; padding: 22mm 24mm; overflow: hidden; }

  /* HEADER */
  .head { margin-bottom: 10mm; text-align: center; }
  .head .kicker { letter-spacing: 8px; font-weight: 700; font-size: 13pt; }
  .head h1 { margin: 4mm 0 2mm; font-size: 78pt; letter-spacing: -2px; line-height: 0.95; font-weight: 900; }
  .head .sub { font-size: 20pt; font-weight: 500; }
  .head .edition { display:inline-block; margin-top: 3mm; padding: 2mm 6mm; border: 0.8mm solid #000; font-weight: 800; font-size: 18pt; letter-spacing: 2px; }

  /* SECTIONS */
  .section { margin: 10mm 0 6mm; text-align: center; }
  .section-title { font-size: 28pt; font-weight: 900; letter-spacing: 3px; border-top: 1.5mm solid #000; border-bottom: 1.5mm solid #000; padding: 3mm 0; margin: 0 auto 6mm; max-width: 500mm; }

  /* GROUPS */
  .groups { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14mm; max-width: 760mm; margin: 0 auto; }
  .group-card { border: 0.6mm solid #000; padding: 0; background: #fff; text-align: left; }
  .group-title { background: #000; color: #fff; text-align: center; padding: 5mm 6mm; font-size: 24pt; font-weight: 900; letter-spacing: 3px; }
  .group-table { width: 100%; border-collapse: collapse; }
  .group-table th, .group-table td { border-bottom: 0.3mm solid #000; padding: 4mm; vertical-align: middle; }
  .group-table th { background: #f4f4f4; text-transform: uppercase; font-size: 11pt; letter-spacing: 1px; }
  .group-table td.pos { width: 8mm; text-align: center; font-weight: 700; font-size: 16pt; }
  .group-table td.tname { font-weight: 700; font-size: 18pt; min-width: 50mm; }
  .pt-col { padding: 2mm 4mm !important; }
  .pt-row { display: flex; gap: 5mm; align-items: center; justify-content: flex-end; }
  .pt-group { display: flex; gap: 2mm; padding: 1mm 0; }
  .pt-square { display: inline-block; width: 10mm; height: 10mm; border: 0.5mm solid #000; }
  .pt-label { font-size: 9pt; font-weight: 700; letter-spacing: 1px; }
  .pt-tot { display: flex; flex-direction: column; align-items: center; gap: 1mm; margin-left: 4mm; }
  .pt-tot-box { width: 20mm; height: 11mm; border: 0.7mm solid #000; }

  /* GROUP MATCHES */
  .group-matches-title { padding: 5mm 6mm 2mm; font-weight: 800; font-size: 14pt; letter-spacing: 2px; text-transform: uppercase; border-top: 0.6mm solid #000; }
  .group-matches { padding: 0 6mm 5mm; }
  .mrow { display: grid; grid-template-columns: 1fr auto 1fr; column-gap: 3mm; row-gap: 1mm; align-items: center; padding: 3mm 0; border-bottom: 0.2mm dashed #888; }
  .mrow:last-child { border-bottom: none; }
  .mteam { font-size: 13pt; font-weight: 600; }
  .mteam.home { text-align: right; }
  .mteam.away { text-align: left; }
  .mscore { display: flex; gap: 2mm; align-items: center; }
  .mbox { display: inline-block; width: 12mm; height: 9mm; border: 0.4mm solid #000; }
  .msep { font-size: 14pt; font-weight: 700; }
  .mdate { grid-column: 1 / -1; text-align: center; font-size: 9pt; color: #333; margin-top: 1mm; }

  /* KNOCKOUT */
  .bracket { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16mm; align-items: stretch; max-width: 720mm; margin: 0 auto; }
  .col-title { font-size: 16pt; font-weight: 900; letter-spacing: 3px; margin-bottom: 4mm; text-align: center; border-bottom: 0.4mm solid #000; padding-bottom: 2mm; }
  .col-qf, .col-sf, .col-fin { display: flex; flex-direction: column; align-items: stretch; justify-content: center; gap: 8mm; }

  .kbox { border: 0.6mm solid #000; padding: 4mm 5mm; background: #fff; min-height: 60mm; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; gap: 2.5mm; }
  .klabel { font-size: 10pt; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 4mm; text-align: center; }
  .kteams { display: grid; grid-template-columns: 1fr auto 1fr; gap: 3mm; align-items: center; width: 100%; }
  .kteam { font-size: 14pt; font-weight: 700; text-align: right; }
  .kteam.right { text-align: left; }
  .kteam-slot { display: flex; flex-direction: column; align-items: stretch; gap: 1mm; }
  .kteam-slot.right { text-align: left; }
  .kteam-slot:not(.right) { text-align: right; }
  .kline { height: 10mm; border-bottom: 0.4mm solid #000; }
  .khint { font-size: 9pt; color: #666; font-style: italic; }
  .kscore { display: flex; gap: 2mm; align-items: center; justify-content: center; }
  .kscore-col { display: flex; flex-direction: column; gap: 2mm; align-items: center; }
  .kleg-row { display: flex; gap: 2mm; align-items: center; }
  .kleg-tag { font-size: 9pt; font-weight: 800; letter-spacing: 1px; min-width: 6mm; text-align: center; }
  .kbox-score { display: inline-block; width: 18mm; height: 13mm; border: 0.6mm solid #000; }
  .ksep { font-size: 16pt; font-weight: 700; }
  .kdate { margin: 0 auto 4mm; font-size: 12pt; font-weight: 700; padding: 2mm 4mm; border: 0.3mm dashed #555; text-align: center; display: inline-block; }
  .kdates { margin: 0 auto 4mm; padding: 2mm 4mm; border: 0.3mm dashed #555; display: flex; flex-direction: column; gap: 1.5mm; font-size: 11pt; }
  .kdate-row { display: flex; justify-content: center; gap: 3mm; align-items: baseline; }
  .kdate-row .kleg-tag { font-size: 9.5pt; letter-spacing: 1px; text-transform: uppercase; min-width: 0; }
  .kdate-row .kdate-val { font-weight: 700; }

  .kbox.final-1 { border-width: 1.2mm; min-height: 58mm; }
  .kbox.final-1 .klabel { font-size: 14pt; }
  .kbox.final-1 .kteam { font-size: 18pt; }

  .footer { margin-top: 24mm; text-align: center; font-size: 11pt; border-top: 0.4mm solid #000; padding-top: 6mm; }

  @media print {
    html, body, .sheet { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style></head>
<body>
  <div class="sheet">
    <div class="head">
      <div class="kicker">VIADANA BRESCIANA · CALCIO A 6</div>
      <h1>TORNEO NOTTURNO</h1>
      <div class="sub">Tabellone Ufficiale</div>
      <div class="edition">30ª EDIZIONE · 2026</div>
    </div>

    <div class="section">
      <div class="section-title">FASE A GIRONI</div>
      <div class="groups">
        ${groupBlocks.length ? groupBlocks.map(groupCard).join('') : `
          <div class="group-card"><div class="group-title">GIRONE A</div></div>
          <div class="group-card"><div class="group-title">GIRONE B</div></div>
          <div class="group-card"><div class="group-title">GIRONE C</div></div>
        `}
      </div>
    </div>

    <div class="section">
      <div class="section-title">FASE FINALE</div>
      <div class="bracket">
        <div class="col-qf">
          <div class="col-title">QUARTI</div>
          ${kMatchBox(qfData[0], 'QF1')}
          ${kMatchBox(qfData[1], 'QF2')}
          ${kMatchBox(qfData[2], 'QF3')}
          ${kMatchBox(qfData[3], 'QF4')}
        </div>
        <div class="col-sf">
          <div class="col-title">SEMIFINALI</div>
          ${kMatchBox(sfData[0], 'SF1')}
          ${kMatchBox(sfData[1], 'SF2')}
        </div>
        <div class="col-fin">
          <div class="col-title">FINALI</div>
          ${kMatchBox(final34, 'Finale 3° / 4° posto')}
          ${kMatchBox(finalMatch, 'Finale 1° / 2° posto').replace('class="kbox"', 'class="kbox final-1"')}
        </div>
      </div>
    </div>
  </div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-tabellone">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1 flex items-center gap-3">
              <LayoutGrid className="w-7 h-7 text-primary" />
              TABELLONE
            </h1>
            <p className="text-slate-400 text-sm">Visualizzazione bracket fase eliminatoria</p>
          </div>
          <Button
            onClick={printA0}
            className="bg-gold text-slate-950 hover:bg-gold/90"
            data-testid="print-a0-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Stampa Tabellone A0
          </Button>
        </div>

        {!hasKnockout ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center">
              <Trophy className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <h2 className="font-heading text-xl mb-2">Fase eliminatoria non ancora iniziata</h2>
              <p className="text-slate-400 mb-6">Il tabellone sarà disponibile al termine della fase a gironi</p>

              {qualifiedTeams.length > 0 && groupPhaseComplete && (
                <div className="mt-8">
                  <h3 className="font-heading text-lg mb-4">Classifica attuale dei gironi</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {qualifiedTeams.map((q) => (
                      <div key={q.group} className="bg-slate-800/50 rounded-lg p-4">
                        <div className="text-sm text-primary font-bold mb-2">Girone {q.group}</div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-primary/20 text-primary text-xs flex items-center justify-center">1</span>
                            <span className="text-sm truncate">{q.first?.name || '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-slate-700 text-slate-400 text-xs flex items-center justify-center">2</span>
                            <span className="text-sm truncate">{q.second?.name || '-'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Visual Bracket */}
            <div className="overflow-x-auto">
              <div className="min-w-[900px] p-4">
                <div className="grid grid-cols-4 gap-8">
                  {/* Quarterfinals */}
                  <div className="space-y-4">
                    <h3 className="font-heading text-lg text-center text-primary mb-4">QUARTI</h3>
                    {Object.entries(qfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="space-y-2">
                        <div className="text-xs text-slate-500 text-center">Q{num}</div>
                        <BracketMatch match={leg1} />
                        <BracketMatch match={leg2} />
                      </div>
                    ))}
                  </div>

                  {/* Semifinals */}
                  <div className="space-y-4 pt-16">
                    <h3 className="font-heading text-lg text-center text-gold mb-4">SEMIFINALI</h3>
                    {Object.entries(sfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="space-y-2">
                        <div className="text-xs text-slate-500 text-center">SF{num}</div>
                        <BracketMatch match={leg1} />
                        <BracketMatch match={leg2} />
                      </div>
                    ))}
                  </div>

                  {/* 3rd/4th */}
                  <div className="space-y-4 pt-24">
                    <h3 className="font-heading text-lg text-center text-slate-400 mb-4">3/4 POSTO</h3>
                    {knockout?.final_3_4 ? (
                      <BracketMatch match={knockout.final_3_4} showLeg={false} />
                    ) : (
                      <div className="bg-slate-900/30 rounded-lg border border-dashed border-slate-700 p-6 text-center text-slate-500">TBD</div>
                    )}
                  </div>

                  {/* Final */}
                  <div className="space-y-4 pt-24">
                    <h3 className="font-heading text-lg text-center mb-4">
                      <span className="text-yellow-400">&#9733;</span> FINALE <span className="text-yellow-400">&#9733;</span>
                    </h3>
                    {knockout?.final_1_2 ? (
                      <div className="border-2 border-gold/30 rounded-lg p-1">
                        <BracketMatch match={knockout.final_1_2} showLeg={false} />
                      </div>
                    ) : (
                      <div className="bg-slate-900/30 rounded-lg border-2 border-dashed border-gold/30 p-6 text-center text-slate-500">TBD</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Match Details Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {Object.keys(qfGroups).length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="font-heading text-primary">Quarti di Finale</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(qfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="p-3 bg-slate-800/30 rounded-lg">
                        <div className="text-xs text-slate-500 mb-2">Quarto #{num}</div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{leg1?.home_team?.name || 'TBD'}</span>
                          <span className="text-slate-500">vs</span>
                          <span className="font-semibold">{leg1?.away_team?.name || 'TBD'}</span>
                        </div>
                        {leg1?.is_played && leg2?.is_played && (
                          <div className="text-xs text-center mt-2 text-primary">
                            Aggregato: {(leg1.home_score || 0) + (leg2.away_score || 0)} - {(leg1.away_score || 0) + (leg2.home_score || 0)}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {Object.keys(sfGroups).length > 0 && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="font-heading text-gold">Semifinali</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(sfGroups).sort(([a], [b]) => Number(a) - Number(b)).map(([num, { leg1, leg2 }]) => (
                      <div key={num} className="p-3 bg-slate-800/30 rounded-lg">
                        <div className="text-xs text-slate-500 mb-2">Semifinale #{num}</div>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{leg1?.home_team?.name || 'TBD'}</span>
                          <span className="text-slate-500">vs</span>
                          <span className="font-semibold">{leg1?.away_team?.name || 'TBD'}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTabellonePage;
